import { useCallback, useEffect, useState } from 'react';

const GOOGLE_CLIENT_ID = '262323088934-mfm6gi0h5c2kk4ggd0onfohc0688oqcr.apps.googleusercontent.com';

export function useGoogleAuth({ onSuccess, onError }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Wait for Google Identity Services to load
    const checkGoogleLoaded = () => {
      if (window.google?.accounts?.id) {
        setIsReady(true);
        return true;
      }
      return false;
    };

    if (checkGoogleLoaded()) return;

    // Poll until loaded
    const interval = setInterval(() => {
      if (checkGoogleLoaded()) {
        clearInterval(interval);
      }
    }, 100);

    // Timeout after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      console.error('Google Identity Services failed to load');
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  const handleCredentialResponse = useCallback(async (response) => {
    console.log('🔐 Google: Credential response received');
    setIsLoading(true);
    
    try {
      // Send the ID token to our backend
      const apiUrl = `${window.location.origin}/api/auth/google/token`;
      console.log('📡 Google: Sending token to backend:', apiUrl);
      
      const backendResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken: response.credential }),
      });

      console.log('📡 Google: Backend response status:', backendResponse.status);

      if (!backendResponse.ok) {
        const errorData = await backendResponse.json();
        throw new Error(errorData.error || 'Authentication failed');
      }

      const data = await backendResponse.json();
      console.log('✅ Google: Authentication successful');
      
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      console.error('❌ Google: Authentication error:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [onSuccess, onError]);

  const signIn = useCallback(() => {
    if (!isReady || !window.google?.accounts?.id) {
      console.error('Google Identity Services not ready');
      if (onError) {
        onError(new Error('Google Identity Services not ready'));
      }
      return;
    }

    console.log('🔐 Google: Initializing sign-in...');

    // Initialize Google Identity Services
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    // Show the One Tap prompt or popup
    window.google.accounts.id.prompt((notification) => {
      console.log('🔐 Google: Prompt notification:', notification.getMomentType());
      
      if (notification.isNotDisplayed()) {
        console.log('⚠️ Google: One Tap not displayed, reason:', notification.getNotDisplayedReason());
        // Fall back to popup button click
        showPopup();
      } else if (notification.isSkippedMoment()) {
        console.log('⚠️ Google: One Tap skipped, reason:', notification.getSkippedReason());
      } else if (notification.isDismissedMoment()) {
        console.log('⚠️ Google: One Tap dismissed, reason:', notification.getDismissedReason());
      }
    });
  }, [isReady, handleCredentialResponse, onError]);

  const showPopup = useCallback(() => {
    if (!window.google?.accounts?.oauth2) {
      console.error('Google OAuth2 not available');
      return;
    }

    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'email profile',
      ux_mode: 'popup',
      callback: async (response) => {
        if (response.code) {
          // Exchange code for tokens via backend
          setIsLoading(true);
          try {
            const apiUrl = `${window.location.origin}/api/auth/google/code`;
            const backendResponse = await fetch(apiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: response.code }),
            });

            if (!backendResponse.ok) {
              const errorData = await backendResponse.json();
              throw new Error(errorData.error || 'Authentication failed');
            }

            const data = await backendResponse.json();
            if (onSuccess) onSuccess(data);
          } catch (error) {
            if (onError) onError(error);
          } finally {
            setIsLoading(false);
          }
        }
      },
    });

    client.requestCode();
  }, [onSuccess, onError]);

  return {
    signIn,
    isLoading,
    isReady,
  };
}

export default useGoogleAuth;
